import { ChangeDetectionStrategy, Component, computed, inject, Input, Signal, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { ClientType } from 'src/app/core/state/types';
import { LundifySubscriptionFacade } from './services/lundify-subscription.facade';
import { LundifyUsersFacade } from './services/lundify-users.facade';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';
import { LundifyEmailPortalFacade } from './services/lundify-emailportal.facade';
import { of, switchMap } from 'rxjs';

@Component({
  selector: 'app-lundify-subscription',
  templateUrl: './lundify-subscription.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    LundifySubscriptionFacade,
    LundifyUsersFacade,
    LundifyEmailPortalFacade,
    getTranslationProvider('LundifySubscriptionComponent'),
  ],
})
export class LundifySubscriptionComponent extends TranslatableComponent {
  @Input() client: ClientType;
  @Input()
  get active(): boolean {
    return this.internalActiveValue;
  }
  set active(value: boolean) {
    this.internalActiveValue = value;
    if (value && this.client?.cloudApiKey) {
      this.loadSubscriptionData(this.client.id);
      return;
    }
    this.lundifySubscriptionFacade.resetSubscriptionData();
  }

  get hasError(): Signal<boolean> {
    return this.lundifySubscriptionFacade.hasError;
  }

  get isArchived(): Signal<boolean> {
    return this.lundifySubscriptionFacade.isArchived;
  }

  errorMessage$: Observable<string>;

  get isCurrentUserClientResponsibleOrAdmin(): Signal<boolean> {
    if (!this.client) {
      return signal(false);
    }
    return this.lundifySubscriptionFacade.hasPermissionToEdit(this.client.responsible?.id);
  }

  get hasEmailPortal(): Signal<boolean> {
    return this.lundifyEmailPortalFacade.hasEmailPortalAddress;
  }

  get emailPortalAddress(): Signal<string | null> {
    return this.lundifyEmailPortalFacade.emailPortalAddress;
  }

  private internalActiveValue: boolean;
  private lundifySubscriptionFacade: LundifySubscriptionFacade;
  private lundifyUsersFacade: LundifyUsersFacade;
  private lundifyEmailPortalFacade: LundifyEmailPortalFacade;

  constructor() {
    super();
    this.lundifySubscriptionFacade = inject(LundifySubscriptionFacade);
    this.lundifyUsersFacade = inject(LundifyUsersFacade);
    this.errorMessage$ = toObservable(this.getErrorMessage());
    this.lundifyEmailPortalFacade = inject(LundifyEmailPortalFacade);
  }

  canShowSubscriptionInfo(): boolean {
    return (
      !!this.client?.cloudApiKey &&
      (this.lundifySubscriptionFacade.isArchived() || this.lundifySubscriptionFacade.hasSubscriptionTypeId())
    );
  }

  private getErrorMessage(): Signal<string> {
    return computed(() => {
      const errorType = this.lundifySubscriptionFacade.errorType();
      if (errorType === 'markedForDeletion') {
        return this.translate('deletedDatabaseErrorMessage');
      }
      return this.translate('errorLoadingSubscriptionContent');
    });
  }

  copyEmailToClipboard(): void {
    const email = this.lundifyEmailPortalFacade.emailPortalAddress();
    navigator.clipboard.writeText(email).then(() => {
      this.store.dispatch(ToastActions.showInfoMessage({ summary: this.translate('emailCopiedToClipboard') }));
    });
  }

  private loadSubscriptionData(clientId: number): void {
    // error handling is done inside the facade
    this.lundifySubscriptionFacade
      .loadSubscriptionInformation(clientId)
      .pipe(
        switchMap(() => {
          if (this.lundifySubscriptionFacade.hasEmailPortalIntegration()) {
            return this.lundifyEmailPortalFacade.loadEmailPortalAddress(clientId);
          }
          return of(null);
        }),
      )
      .subscribe();

    // error handling is done inside the facade
    this.lundifyUsersFacade.loadSubscriptionUsers(clientId).subscribe();
  }
}
